import {
  ILabelPlateSpec,
  labelPlateFit,
} from '~/client/src/shared/map/labelPlateSpec'

// Painting a plate: the 9-patch its spec implies, and the canvas work that fills it. The spec
// itself, and the fit arithmetic, are data — they live in shared/map/labelPlateSpec.

// Enough middle for the stretch to survive rounding at any pixel ratio.
const MIN_STRETCH_BAND_PX = 8

export interface ILabelPlateGeometry {
  width: number
  height: number
  radius: number
  lead: number
  metadata: {
    pixelRatio: number
    stretchX: Array<[number, number]>
    stretchY: Array<[number, number]>
    content: [number, number, number, number]
  }
}

// One source for the whole patch: every part rounded once, the image sized from the rounded
// parts. Rounding independently lets a band round to zero below 1x, zeroing the stretch total so
// the fit divides by it.
//
// `content` is where mapbox fits the TEXT, so with a chip it starts after the chip's lead — span
// the full image and the text lands straight over the logo. edgeScale pre-divides the radius by
// the band's factor, so icon-size lands the corners on the LABEL's curve.
export function labelPlateGeometry(
  ratio: number,
  spec: ILabelPlateSpec,
  edgeScale = 1,
  // A floor on the image's FIXED width, in spec pixels. Fixed 9-patch material is emitted at
  // constant pixel size however short the text is — mapbox prices it as `em * fontScale + px`
  // and the fixed half is the px term — so widening the fixed columns gives the fitted quad a
  // minimum width. The selection outline needs one: it has to span the marker circle even
  // around a two-character label, which on its own would fit a narrower box than the circle.
  minFixedWidthPx = 0,
  // A floor on the non-stretching MARGIN, in spec pixels. A stroked 9-patch needs one: the
  // stroke is painted into the image's edge, and anything thicker than the fixed rows spills
  // into the stretch zone and is smeared across the whole label. Separate from the radius on
  // purpose — that is the corner CURVE, this is how much of the image does not stretch.
  minFixedEdgePx = 0,
): ILabelPlateGeometry {
  const radius = Math.max(1, Math.round(spec.radiusPx * edgeScale * ratio))
  const band = Math.max(1, Math.round(MIN_STRETCH_BAND_PX * ratio))
  // On edgeScale with the radius. The chip and the lead are FIXED 9-patch material, so a plate
  // baked for a low band that kept them at full size could not fit a box smaller than they are:
  // the plate stopped shrinking with its label and stood off it, several times too big.
  const chip = spec.chipPx ? Math.round(spec.chipPx * edgeScale * ratio) : 0
  const naturalLead = spec.chipPx
    ? Math.round(labelPlateFit(spec).leadPx * edgeScale * ratio)
    : radius
  // Split evenly, so the box the outline draws stays centred on the text it wraps.
  const floorSide = Math.max(
    0,
    Math.round((minFixedWidthPx * ratio - (naturalLead + radius)) / 2),
  )
  const lead = naturalLead + floorSide
  const rightFixed = radius + floorSide

  const edge = Math.max(radius, Math.round(minFixedEdgePx * ratio))

  const width = lead + band + rightFixed
  // Corner rows are held OUT of the stretch zones, or the bands absorb every change in label
  // height and the same arc renders square on one line and stretched on three. Out of `content`
  // as well, and that part is load-bearing: mapbox counts fixedContentHeight only from inside the
  // content box, so corners in there would raise it past the chip and inflate the padding. They
  // are emitted at fixed size around the fitted box, which is why minPlatePadYPx floors at zero.
  const height = chip ? edge * 2 + band * 2 + chip : edge * 2 + band

  const stretchY: Array<[number, number]> = chip
    ? [
        [edge, edge + band],
        [edge + band + chip, height - edge],
      ]
    : [[edge, edge + band]]

  return {
    width,
    height,
    radius,
    lead,
    metadata: {
      pixelRatio: ratio,
      stretchX: [[lead, lead + band]],
      stretchY,
      content: [chip ? lead : 0, edge, width, height - edge],
    },
  }
}
// A selection ring, drawn INSIDE the plate's own edge so selecting a label cannot change the
// footprint it collides with.
//
// It survives the 9-patch because a stroke keeps its thickness when stretched ALONG its length,
// which is what happens here — and the corners, which would have wrecked it, no longer stretch.
export interface ILabelPlateStroke {
  widthPx: number
  color: string
  // The coat the ring wears against the map — the same rule pin strokes and label halos follow.
  // Always the LIGHT one, never tone-matched: blue reads far better over it on every ground, and
  // its worst case is over LIGHT ground, so measuring would pick dark exactly where blue fails.
  casingPx?: number
  casingColor?: string
}

// roundRect is not in every runtime this ships to, so the path is written out. `inset` pulls it
// in from the image edge, which is how the ring is held inside the plate.
function tracePlate(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  width: number,
  height: number,
  radius: number,
  inset: number,
): void {
  const x = inset
  const y = inset
  const w = width - inset * 2
  const h = height - inset * 2
  const r = Math.max(0, Math.min(radius - inset, w / 2, h / 2))
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.arcTo(x + w, y, x + w, y + r, r)
  ctx.lineTo(x + w, y + h - r)
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r)
  ctx.lineTo(x + r, y + h)
  ctx.arcTo(x, y + h, x, y + h - r, r)
  ctx.lineTo(x, y + r)
  ctx.arcTo(x, y, x + r, y, r)
  ctx.closePath()
}

export function drawLabelPlate(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  geometry: Pick<ILabelPlateGeometry, 'width' | 'height' | 'radius'>,
  spec: ILabelPlateSpec,
  stroke?: ILabelPlateStroke,
): void {
  const { width, height, radius } = geometry
  ctx.clearRect(0, 0, width, height)

  tracePlate(ctx, width, height, radius, 0)
  ctx.fillStyle = spec.fill
  ctx.fill()

  if (!stroke) {
    return
  }
  // Scaled by the same ratio the image is: geometry.radius is already spec.radiusPx through the
  // pixel ratio and the zoom band, so the ring tracks whatever the plate's corners did.
  const scale = radius / spec.radiusPx
  const ring = stroke.widthPx * scale
  const casing = (stroke.casingPx ?? 0) * scale

  if (casing > 0 && stroke.casingColor) {
    // Wider than the ring by the casing, not by twice it: the ring is then painted over the
    // inner half, leaving the coat visible on the OUTSIDE only. Against the map is where the
    // contrast is needed; inside the box there is nothing to separate the ring from.
    const outer = ring + casing
    tracePlate(ctx, width, height, radius, outer / 2)
    ctx.lineWidth = outer
    ctx.strokeStyle = stroke.casingColor
    ctx.stroke()
  }

  tracePlate(ctx, width, height, radius, casing + ring / 2)
  ctx.lineWidth = ring
  ctx.strokeStyle = stroke.color
  ctx.stroke()
}
